import { Loader2, PlayIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Virtuoso,
  type Components,
  VirtuosoHandle,
  type ScrollSeekConfiguration,
  type ScrollSeekPlaceholderProps,
} from "react-virtuoso";

import ToolCallStatusShimmer from "../../Messages/Message/ToolCallStatusShimmer";
import { type IPlayground, usePlayground } from "../../playground-store";
import PlaygroundTourFakeOutputCard from "../../tour/PlaygroundTourFakeOutputCard";
import DrawerEmptyState from "../DrawerEmptyState";
import DrawerWidget from "../DrawerWidget";

import useInitialOutputs from "./hooks/useInitialOutputs";
import OutputCard from "./OutputCard";
import { withOutputMessages } from "./outputs-widget-context";

import LoadingSpinner from "@/components/LoadingSpinner";
import { ProviderApiKeyPanel } from "@/components/ProviderApiKeyPrompt";
import ErrorDisplay from "@/components/ui/ErrorDisplay";
import { Skeleton } from "@/components/ui/skeleton";
import { useOptionalPlaygroundTourContext } from "@/components/Playground/tour/context";
import { TOUR_TARGET_IDS } from "@/components/Playground/tour/constants";
import type { RunGroupsCursor } from "@/queries/playground/useSessionRuns";

const getOutputKey = (output: IPlayground) => output.currentOperation?.id ?? output.instanceId;
const OUTPUT_ROW_ESTIMATED_HEIGHT = 360;

const OutputCardSurface = ({ children }: { children: React.ReactNode }) => (
  <div className="overflow-hidden rounded-xl border border-gray-200/70 bg-white/95 shadow-none ring-1 ring-black/[0.02]">
    {children}
  </div>
);

const OutputCardSkeleton = ({ infoText }: { infoText?: string }) => (
  <OutputCardSurface>
    <div className="flex items-center justify-between border-b border-gray-100/80 bg-white/90 px-3 py-2.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-4 w-16 rounded" />
        <Skeleton className="h-3 w-6 rounded" />
        <Skeleton className="h-7 w-32 rounded-full" />
      </div>
      <Skeleton className="h-3 w-14 rounded" />
    </div>
    <div className="bg-gray-50/60 px-2.5 py-2.5">
      <ToolCallStatusShimmer
        status="Generating..."
        className="min-h-[200px] w-full rounded-lg border border-gray-100 bg-white"
        infoText={infoText}
      />
    </div>
  </OutputCardSurface>
);

interface BaseOutputListItem {
  id: string;
  autoFit?: boolean;
}

type OutputListItem =
  | (BaseOutputListItem & { type: "tour-fake-output" })
  | (BaseOutputListItem & { type: "empty" })
  | (BaseOutputListItem & { type: "pending"; infoText?: string })
  | (BaseOutputListItem & { type: "api-key"; provider: string })
  | (BaseOutputListItem & { type: "error"; message: string })
  | (BaseOutputListItem & {
      type: "output";
      output: IPlayground;
      outputKey: string;
      outputNumber: number;
      shouldShowOutputCard: boolean;
      infoText?: string;
      isCollapsed: boolean;
    });

interface OutputListContext {
  onBatchApiKeySaved: () => void;
  onOutputCollapsedChange: (outputKey: string, isCollapsed: boolean) => void;
}

interface OutputListRowProps {
  item: OutputListItem;
  context: OutputListContext;
}

const OutputListRow = ({ item, context }: OutputListRowProps) => {
  const autoFitMarker = item.autoFit ? "" : undefined;

  if (item.type === "tour-fake-output") {
    return (
      <div className="pb-1 [overflow-anchor:none]">
        <PlaygroundTourFakeOutputCard />
      </div>
    );
  }

  if (item.type === "empty") {
    return (
      <div className="pb-1 [overflow-anchor:none]">
        <DrawerEmptyState
          icon={<PlayIcon />}
          title="No outputs yet"
          description="Run the prompt to see outputs here."
        />
      </div>
    );
  }

  if (item.type === "pending") {
    return (
      <div
        className="pb-1 [overflow-anchor:none]"
        data-playground-outputs-auto-fit-content={autoFitMarker}
      >
        <OutputCardSkeleton infoText={item.infoText} />
      </div>
    );
  }

  if (item.type === "api-key") {
    return (
      <div className="flex w-full flex-col gap-y-2 p-2 pb-3 [overflow-anchor:none]">
        <ProviderApiKeyPanel
          provider={item.provider}
          onAfterProviderKeySaved={context.onBatchApiKeySaved}
        />
      </div>
    );
  }

  if (item.type === "error") {
    return (
      <div
        className="pb-1 [overflow-anchor:none]"
        data-playground-outputs-auto-fit-content={autoFitMarker}
      >
        <OutputCardSurface>
          <div className="bg-gray-50/60 p-2.5">
            <ErrorDisplay>{item.message}</ErrorDisplay>
          </div>
        </OutputCardSurface>
      </div>
    );
  }

  return (
    <div
      className="pb-1 [overflow-anchor:none]"
      data-playground-outputs-auto-fit-content={autoFitMarker}
    >
      {item.shouldShowOutputCard ? (
        <OutputCard
          output={item.output}
          outputNumber={item.outputNumber}
          isCollapsed={item.isCollapsed}
          onCollapsedChange={isCollapsed =>
            context.onOutputCollapsedChange(item.outputKey, isCollapsed)
          }
        />
      ) : (
        <OutputCardSkeleton infoText={item.infoText} />
      )}
    </div>
  );
};

const computeOutputListItemKey = (_: number, item: OutputListItem) => item.id;

const renderOutputListItem = (_: number, item: OutputListItem, context: OutputListContext) => (
  <OutputListRow item={item} context={context} />
);

const OutputScrollSeekPlaceholder = ({ height }: ScrollSeekPlaceholderProps) => (
  <div
    className="px-0 pb-1 [overflow-anchor:none]"
    style={{ height: Math.max(height, OUTPUT_ROW_ESTIMATED_HEIGHT) }}
  >
    <OutputCardSurface>
      <div className="flex items-center justify-between border-b border-gray-100/80 bg-white/90 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-16 rounded" />
          <Skeleton className="h-3 w-6 rounded" />
          <Skeleton className="h-7 w-32 rounded-full" />
        </div>
        <Skeleton className="h-3 w-14 rounded" />
      </div>
      <div className="space-y-2 bg-gray-50/60 px-2.5 py-2.5">
        <Skeleton className="h-28 rounded-lg border border-gray-100 bg-white" />
        <Skeleton className="h-3 w-5/6 rounded" />
        <Skeleton className="h-3 w-2/3 rounded" />
      </div>
    </OutputCardSurface>
  </div>
);

const outputVirtuosoComponents: Components<OutputListItem, OutputListContext> = {
  ScrollSeekPlaceholder: OutputScrollSeekPlaceholder,
};

const outputScrollSeekConfiguration: ScrollSeekConfiguration = {
  enter: velocity => Math.abs(velocity) > 700,
  exit: velocity => Math.abs(velocity) < 120,
};

const Outputs = () => {
  const {
    outputs,
    localOutputs,
    pendingOutputs,
    hasOutputContent,
    instanceId,
    runGroupError,
    batchProviderApiKeyPrompt,
    clearBatchProviderApiKeyPrompt,
    run,
    isInitializing,
    streamingEnabled,
    isImageGenerationMode,
  } = usePlayground();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const previousPendingOutputsRef = useRef(pendingOutputs);
  const [beforeCursor, setBeforeCursor] = useState<RunGroupsCursor | null>(null);
  const [collapsedOutputIds, setCollapsedOutputIds] = useState<Set<string>>(() => new Set());

  const handleBatchApiKeySaved = useCallback(() => {
    const pending = batchProviderApiKeyPrompt;
    clearBatchProviderApiKeyPrompt();
    if (pending) {
      run(pending.recaptchaResponse);
    }
  }, [batchProviderApiKeyPrompt, clearBatchProviderApiKeyPrompt, run]);

  const handleOutputCollapsedChange = useCallback((outputKey: string, isCollapsed: boolean) => {
    setCollapsedOutputIds(currentCollapsedOutputIds => {
      const nextCollapsedOutputIds = new Set(currentCollapsedOutputIds);

      if (isCollapsed) {
        nextCollapsedOutputIds.add(outputKey);
      } else {
        nextCollapsedOutputIds.delete(outputKey);
      }

      return nextCollapsedOutputIds;
    });
  }, []);

  const tour = useOptionalPlaygroundTourContext();
  const showTourFakeOutput = tour?.hasFakeOutput ?? false;

  const { isFetching, isLoading, total, hasNext, nextCursor } = useInitialOutputs(beforeCursor);
  const displayedTotal = showTourFakeOutput ? Math.max(total, 1) : total;
  const outputInfoText = isImageGenerationMode
    ? "Image generation can take 1-2 minutes."
    : undefined;

  const outputKeys = useMemo(() => new Set(outputs.map(output => getOutputKey(output))), [outputs]);

  useEffect(() => {
    setCollapsedOutputIds(currentCollapsedOutputIds => {
      const nextCollapsedOutputIds = new Set<string>();
      let changed = false;

      currentCollapsedOutputIds.forEach(outputKey => {
        if (outputKeys.has(outputKey)) {
          nextCollapsedOutputIds.add(outputKey);
        } else {
          changed = true;
        }
      });

      return changed ? nextCollapsedOutputIds : currentCollapsedOutputIds;
    });
  }, [outputKeys]);

  const outputListContext = useMemo<OutputListContext>(
    () => ({
      onBatchApiKeySaved: handleBatchApiKeySaved,
      onOutputCollapsedChange: handleOutputCollapsedChange,
    }),
    [handleBatchApiKeySaved, handleOutputCollapsedChange],
  );

  const outputRows = useMemo<OutputListItem[]>(() => {
    const rows: OutputListItem[] = [];

    if (showTourFakeOutput) {
      rows.push({ id: "tour-fake-output", type: "tour-fake-output" });
    }

    if (
      !showTourFakeOutput &&
      !runGroupError &&
      !batchProviderApiKeyPrompt &&
      !hasOutputContent &&
      beforeCursor === null &&
      !isLoading &&
      !isInitializing
    ) {
      rows.push({ id: "empty-placeholder", type: "empty" });
    }

    if (beforeCursor === null && pendingOutputs > 0) {
      Array.from({ length: pendingOutputs }).forEach((_, index) => {
        rows.push({
          id: `pending-output-${index}-${instanceId}`,
          type: "pending",
          infoText: outputInfoText,
        });
      });
    }

    if (batchProviderApiKeyPrompt) {
      rows.push({
        id: "batch-provider-api-key",
        type: "api-key",
        provider: batchProviderApiKeyPrompt.provider,
      });
    }

    if (runGroupError) {
      rows.push({ id: "run-group-error", type: "error", message: runGroupError });
    }

    outputs.forEach((output, index) => {
      const outputKey = getOutputKey(output);
      const shouldShowGeneratingShimmer =
        output.isGeneratingOutput && !output.hasRenderableOutputMessages;
      // Show once we have renderable content (hybrids deliver via websocket even if Stream is off).
      const shouldShowOutputCard =
        !shouldShowGeneratingShimmer &&
        (streamingEnabled || output.hasRunDetailsLoaded || output.hasRenderableOutputMessages);

      rows.push({
        id: `output-${outputKey}`,
        type: "output",
        output,
        outputKey,
        outputNumber: outputs.length - index,
        shouldShowOutputCard,
        infoText: outputInfoText,
        isCollapsed: collapsedOutputIds.has(outputKey),
      });
    });

    const firstOutputFitRow = rows.find(
      row => row.type === "pending" || row.type === "error" || row.type === "output",
    );
    if (firstOutputFitRow) {
      firstOutputFitRow.autoFit = true;
    }

    return rows;
  }, [
    batchProviderApiKeyPrompt,
    beforeCursor,
    collapsedOutputIds,
    hasOutputContent,
    instanceId,
    isInitializing,
    isLoading,
    outputInfoText,
    outputs,
    pendingOutputs,
    runGroupError,
    showTourFakeOutput,
    streamingEnabled,
  ]);

  useEffect(() => {
    const didStartNewPendingOutput = pendingOutputs > previousPendingOutputsRef.current;

    if (outputRows.length > 0 && didStartNewPendingOutput) {
      virtuosoRef.current?.scrollToIndex({ index: 0, align: "start", behavior: "auto" });
    }

    previousPendingOutputsRef.current = pendingOutputs;
  }, [outputRows.length, pendingOutputs]);

  useEffect(() => {
    setBeforeCursor(null);
  }, [localOutputs.length]);

  const handleLoadMoreOutputs = useCallback(() => {
    if (!hasNext || isFetching || !nextCursor) {
      return;
    }

    setBeforeCursor(nextCursor);
  }, [hasNext, isFetching, nextCursor]);

  if (
    !showTourFakeOutput &&
    (isInitializing || (isLoading && beforeCursor === null && !hasOutputContent))
  ) {
    return (
      <DrawerWidget
        name="Outputs"
        count={0}
        hideHeader
        className="mx-3 flex min-h-0 flex-grow flex-col overflow-hidden border-0 bg-transparent p-0 shadow-none"
        contentClassName="flex min-h-0 flex-grow flex-col"
        childrenClassName="flex min-h-0 flex-grow flex-col"
      >
        <div className="flex h-full items-center justify-center py-8">
          <LoadingSpinner size="6" />
        </div>
      </DrawerWidget>
    );
  }

  return (
    <DrawerWidget
      name="Outputs"
      count={displayedTotal}
      id={TOUR_TARGET_IDS.OUTPUTS}
      forceExpanded={tour?.isActive ?? false}
      hideHeader
      className="mx-3 flex min-h-0 flex-grow flex-col overflow-hidden border-0 bg-transparent p-0 shadow-none"
      contentClassName="flex min-h-0 flex-grow flex-col"
      childrenClassName="flex min-h-0 flex-grow flex-col"
    >
      <div className="flex min-h-0 flex-1 flex-col [overflow-anchor:none]">
        <div className="min-h-0 flex-1">
          <Virtuoso
            ref={virtuosoRef}
            data={outputRows}
            style={{ height: "100%" }}
            className="[overflow-anchor:none]"
            components={outputVirtuosoComponents}
            context={outputListContext}
            computeItemKey={computeOutputListItemKey}
            defaultItemHeight={OUTPUT_ROW_ESTIMATED_HEIGHT}
            increaseViewportBy={{ top: 480, bottom: 960 }}
            itemContent={renderOutputListItem}
            overscan={OUTPUT_ROW_ESTIMATED_HEIGHT}
            scrollSeekConfiguration={outputScrollSeekConfiguration}
            endReached={handleLoadMoreOutputs}
          />
        </div>
        {isFetching && (
          <div className="flex shrink-0 items-center justify-center gap-2 border-t border-gray-100/70 bg-gray-50/80 py-2 text-xs text-gray-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>Loading more outputs...</span>
          </div>
        )}
      </div>
    </DrawerWidget>
  );
};

export default withOutputMessages(observer(Outputs));
